import { Badge, Box, HStack, IconButton, Spinner, Text, Tooltip } from '@elkaso-app/web-design';
import { useDocumentPreview } from 'apis/document-vault/use-document-preview';
import React, { useCallback, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiClock, FiEye } from 'react-icons/fi';
import { DocumentStatusBadge } from './document-status-badge';
import { DocumentVersionsModal } from './document-versions-modal';

const categoryLabels: Record<string, string> = {
  BUSINESS_REGISTRATION: 'Business Registration',
  OWNER_IDENTIFICATION: 'Owner Identification',
  FINANCIAL_DOCUMENTS: 'Financial Documents',
};

interface VaultDocument {
  documentTypeId: number;
  documentTypeName: string;
  isRequired: boolean;
  hasExpiry: boolean;
  status: string;
  restaurantDocumentId: number | null;
  currentVersion: any;
  completed: boolean;
}

interface VaultCategory {
  category: string;
  label: string;
  total: number;
  completed: number;
  documents: VaultDocument[];
}

interface VaultCategorySectionProps {
  category: VaultCategory;
  restaurantId: string;
}

export const VaultCategorySection: React.FC<VaultCategorySectionProps> = ({ category, restaurantId }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<VaultDocument | null>(null);
  const [isVersionsModalOpen, setIsVersionsModalOpen] = useState(false);
  const { previewCurrentDocument, loadingId } = useDocumentPreview();

  const handleViewVersions = useCallback((doc: VaultDocument) => {
    setSelectedDoc(doc);
    setIsVersionsModalOpen(true);
  }, []);

  return (
    <Box border='1px' borderColor='gray.200' borderRadius='lg' mb='md' bg='white'>
      {/* Category Header */}
      <HStack
        px='md'
        py='sm'
        justify='space-between'
        cursor='pointer'
        onClick={() => setIsExpanded(!isExpanded)}
        _hover={{ bg: 'gray.50' }}
        borderRadius='lg'>
        <HStack spacing='sm'>
          <Text fontWeight='semibold' fontSize='md'>
            {categoryLabels[category.category] ?? category.label}
          </Text>
          <Badge colorScheme={category.completed === category.total ? 'green' : 'gray'} fontSize='xs'>
            {category.completed}/{category.total} complete
          </Badge>
        </HStack>
        <IconButton
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          icon={isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          size='sm'
          variant='ghost'
        />
      </HStack>

      {/* Documents Table */}
      {isExpanded && (
        <Box overflowX='auto'>
          <Box as='table' w='full' fontSize='sm'>
            <Box as='thead' bg='gray.50'>
              <Box as='tr'>
                <Box as='th' px='md' py='sm' textAlign='left' fontWeight='semibold' color='gray.600'>
                  Document Type
                </Box>
                <Box as='th' px='md' py='sm' textAlign='left' fontWeight='semibold' color='gray.600'>
                  Status
                </Box>
                <Box as='th' px='md' py='sm' textAlign='left' fontWeight='semibold' color='gray.600'>
                  Expiry Date
                </Box>
                <Box as='th' px='md' py='sm' textAlign='left' fontWeight='semibold' color='gray.600'>
                  Current File
                </Box>
                <Box as='th' px='md' py='sm' textAlign='left' fontWeight='semibold' color='gray.600'>
                  Actions
                </Box>
              </Box>
            </Box>
            <Box as='tbody'>
              {category.documents.map((doc) => (
                <Box as='tr' key={doc.documentTypeId} borderTop='1px' borderColor='gray.100'>
                  <Box as='td' px='md' py='sm'>
                    <HStack>
                      <Text>{doc.documentTypeName}</Text>
                      {doc.isRequired && (
                        <Badge colorScheme='red' fontSize='2xs'>
                          Required
                        </Badge>
                      )}
                    </HStack>
                  </Box>
                  <Box as='td' px='md' py='sm'>
                    <DocumentStatusBadge status={doc.status} />
                  </Box>
                  <Box as='td' px='md' py='sm'>
                    <Text color={doc.currentVersion?.expiryDate ? 'gray.800' : 'gray.400'}>
                      {doc.currentVersion?.expiryDate ?? '—'}
                    </Text>
                  </Box>
                  <Box as='td' px='md' py='sm'>
                    {doc.currentVersion ? (
                      <Text
                        fontSize='sm'
                        color='blue.500'
                        cursor='pointer'
                        _hover={{ textDecoration: 'underline' }}
                        onClick={() =>
                          doc.restaurantDocumentId &&
                          previewCurrentDocument(restaurantId, String(doc.restaurantDocumentId))
                        }>
                        {loadingId === `current-${doc.restaurantDocumentId}` ? (
                          <Spinner size='xs' mr='xs' />
                        ) : null}
                        {doc.currentVersion.originalFileName}
                      </Text>
                    ) : (
                      <Text color='gray.400'>—</Text>
                    )}
                  </Box>
                  <Box as='td' px='md' py='sm'>
                    <HStack spacing='xs'>
                      {doc.restaurantDocumentId && doc.currentVersion && (
                        <Tooltip label='Preview' placement='top'>
                          <IconButton
                            aria-label='Preview document'
                            icon={<FiEye />}
                            size='sm'
                            variant='ghost'
                            colorScheme='green'
                            isLoading={loadingId === `current-${doc.restaurantDocumentId}`}
                            onClick={() =>
                              previewCurrentDocument(restaurantId, String(doc.restaurantDocumentId))
                            }
                          />
                        </Tooltip>
                      )}
                      {doc.restaurantDocumentId && (
                        <Tooltip label='View Versions' placement='top'>
                          <IconButton
                            aria-label='View versions'
                            icon={<FiClock />}
                            size='sm'
                            variant='ghost'
                            colorScheme='blue'
                            onClick={() => handleViewVersions(doc)}
                          />
                        </Tooltip>
                      )}
                    </HStack>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* Version History Modal */}
      {selectedDoc && selectedDoc.restaurantDocumentId && (
        <DocumentVersionsModal
          isOpen={isVersionsModalOpen}
          onClose={() => {
            setIsVersionsModalOpen(false);
            setSelectedDoc(null);
          }}
          restaurantId={restaurantId}
          documentId={String(selectedDoc.restaurantDocumentId)}
          documentTypeName={selectedDoc.documentTypeName}
        />
      )}
    </Box>
  );
};
